// Line Entry — the designer screens for one LINE form (tree node "Line
// Entry"), laid out as the "Designer — Line Entry" canvas board:
//
//   centre  LineEntryPanel      entry view · sources · buttons (★ face, drag
//                               order, live preview) · header change · quick add
//   right   LineCommandPanel    the selected button's properties
//
// Stores `sql_model.line_entry`. Model: docs/plans/line-entry-development-plan.md §2.

import { useMemo, useState } from 'react'
import { Plus, Star, Trash2 } from 'lucide-react'
import { allSqlModels, headerFormNo, childFormsOf } from '@twasta/page-runtime/txn/utils'
import { legacyActionToDef } from './types'
import type {
  LineCommand, LineEntry, LineSource, LineSourceKind, SqlModel, TransactionModel, TreeNode,
} from './types'
import {
  LineEntryMenu, describeCommand, lineEntryOf, toolbarLayout,
} from '@twasta/page-runtime/txn/lineEntry'
import LineSourceEditor, { emptySource, type Tab as SourceTab } from './LineSourceEditor'

// ── shared look (the canvas's cards) ─────────────────────────────────────
const C = {
  border: 'var(--color-border)',
  muted: 'var(--color-text-secondary)',
  accent: 'var(--color-accent)',
  bg: 'var(--color-bg)',
  band: 'var(--color-bg-secondary)',
  mono: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
}
const card: React.CSSProperties = {
  background: C.bg, border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 16px',
  display: 'flex', flexDirection: 'column', gap: 10,
}
const sec: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, letterSpacing: '.05em', textTransform: 'uppercase', color: C.muted, margin: 0,
}
const li: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: '18px 30px minmax(0,1fr) auto', gap: 8, alignItems: 'center',
  minHeight: 40, padding: '0 10px', border: `1px solid ${C.border}`, borderRadius: 7, fontSize: 14,
  background: C.bg,
}
const btn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px', borderRadius: 6,
  border: `1px solid ${C.border}`, background: C.bg, color: 'var(--color-text)', fontSize: 13,
  fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
}
const linkBtn: React.CSSProperties = {
  background: 'none', border: 0, padding: 0, color: C.accent, cursor: 'pointer', fontSize: 14, fontFamily: 'inherit',
}
const opt: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, cursor: 'pointer' }
const small: React.CSSProperties = { display: 'block', color: C.muted, fontSize: 13 }
const fld: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 5, fontSize: 13, color: C.muted, fontWeight: 500,
}
const inp: React.CSSProperties = {
  height: 32, boxSizing: 'border-box', border: `1px solid ${C.border}`, borderRadius: 6, padding: '0 10px',
  fontSize: 14, color: 'var(--color-text)', background: C.bg, fontFamily: 'inherit', width: '100%',
}

const KIND_BADGE: Record<LineSourceKind, { tag: string; label: string }> = {
  transaction: { tag: 'TXN', label: 'another transaction' },
  master: { tag: 'TBL', label: 'master' },
  business_logic: { tag: 'BL', label: 'business logic' },
  process: { tag: 'GP', label: 'General Process' },
}

function sourceSummary(s: LineSource): string {
  const f = s.from || ({} as LineSource['from'])
  const what = f.kind === 'transaction' ? `${f.object || '?'} form ${f.form_no || '1'}`
    : f.kind === 'master' ? (f.lookup_of ? `from the ${f.lookup_of} lookup` : f.table || '?')
      : f.kind === 'business_logic' ? (f.business_logic?.method || '?') : f.process || '?'
  const parts = [`${KIND_BADGE[f.kind]?.label || f.kind} ${what}`]
  if (s.link?.to?.length) parts.push(`link ${s.link.to.join(' + ')}`)
  const lim = Object.entries(s.limits || {}).map(([col, l]) => {
    const tol = l.tolerance_pct != null ? `${l.tolerance_pct}% over, ` : l.tolerance != null ? `±${l.tolerance}, ` : ''
    return `${col} (${tol}${l.over || 'block'})`
  })
  if (lim.length) parts.push(`limits: ${lim.join(', ')}`)
  return parts.join(' · ')
}

function uniqueName(base: string, taken: string[]): string {
  let n = base
  let i = 2
  while (taken.includes(n)) n = `${base}_${i++}`
  return n
}

function cloneModel(m: TransactionModel): TransactionModel {
  return JSON.parse(JSON.stringify(m))
}

/** Write a form's line_entry (dropping it when nothing is left). */
function writeLineEntry(
  model: TransactionModel, formIndex: number, next: LineEntry | undefined,
  onModelChange: (m: TransactionModel) => void,
) {
  const m = cloneModel(model)
  const target = m.sql_models[formIndex].sql_model
  const empty = !next || (!(next.sources || []).length && !(next.commands || []).length
    && !next.on_header_change && !next.quick_add && !next.view && !next.toolbar)
  if (empty) delete target.line_entry
  else target.line_entry = next
  onModelChange(m)
}

function labelsOf(sm: SqlModel): (col: string) => string {
  const by = new Map<string, string>()
  for (const w of sm.columns || []) {
    const c = w?.column
    if (!c) continue
    const h = String(c.heading || c.name)
    by.set(String(c.name).toLowerCase(), h)
    by.set(String(c.name).replace(/\s+/g, '_').toLowerCase(), h)
  }
  return (col: string) => by.get(String(col).toLowerCase()) || col
}

// ── centre: the Line Entry cards ──────────────────────────────────────────

interface PanelProps {
  model: TransactionModel
  formIndex: number
  parentSm: SqlModel | null
  isHeader: boolean
  project: string
  onModelChange: (m: TransactionModel) => void
  /** Index into `line_entry.commands` of the button shown in the right panel. */
  selectedCommand?: number
  onSelectCommand: (index: number | undefined) => void
  /** Opens something defined elsewhere (a Pick-rows button, a column's
   *  lookup, the form's list settings) — the "every way" list's Edit links. */
  onSelectNode?: (node: TreeNode) => void
}

export default function LineEntryPanel({
  model, formIndex, parentSm, isHeader, project, onModelChange, selectedCommand, onSelectCommand, onSelectNode,
}: PanelProps) {
  const sm = model.sql_models[formIndex]?.sql_model
  const le: LineEntry = sm?.line_entry || {}
  const sources = le.sources || []
  const commands = le.commands || []
  const [editing, setEditing] = useState<{ index: number; source: LineSource } | null>(null)
  const [dragFrom, setDragFrom] = useState<number | null>(null)
  const normalized = useMemo(() => (sm ? lineEntryOf(sm) : null), [sm])
  const layout = useMemo(() => (normalized ? toolbarLayout(normalized) : null), [normalized])

  if (!sm) return null
  if (isHeader) {
    return (
      <div style={{ padding: 24, color: C.muted, fontSize: 14 }}>
        Line Entry belongs on a line (child) form — the lines arrive there.
      </div>
    )
  }

  const patch = (p: Partial<LineEntry>) => writeLineEntry(model, formIndex, { ...le, ...p }, onModelChange)
  const labelOf = labelsOf(sm)
  const parentNames = (parentSm?.columns || []).map(w => w?.column?.name).filter(Boolean) as string[]
  const commandNames = (normalized?.commands || []).map(c => c.name)
  const primary = le.toolbar?.primary || ''
  const implicit = commands.length === 0

  const addSource = (kind: LineSourceKind) => {
    setEditing({ index: -1, source: emptySource(kind, uniqueName(kind === 'master' ? 'items' : 'lines', sources.map(x => x.name))) })
  }
  const saveSource = (s: LineSource) => {
    const next = sources.slice()
    if (editing && editing.index >= 0) {
      const old = next[editing.index].name
      next[editing.index] = s
      const cmds = old !== s.name ? commands.map(c => (c.source === old ? { ...c, source: s.name } : c)) : commands
      patch({ sources: next, commands: cmds })
    } else {
      patch({ sources: [...next, s] })
    }
    setEditing(null)
  }
  const removeSource = (i: number) => {
    const name = sources[i].name
    patch({ sources: sources.filter((_, j) => j !== i), commands: commands.filter(c => c.source !== name) })
    onSelectCommand(undefined)
  }

  // An implicit button (one per source) becomes declared the first time it is
  // touched, so the author edits real data.
  const declared = (): LineCommand[] => (implicit
    ? (normalized?.commands || []).filter(c => !c.synthetic).map(c => ({
      name: c.name, label: c.label, source: c.source,
    }))
    : commands)

  const addCommand = () => {
    if (!sources.length) return
    const base = declared()
    const s = sources[0]
    const name = uniqueName('lines', base.map(c => c.name))
    patch({ commands: [...base, { name, label: s.label || 'Add lines', source: s.name, picker: 'review' }] })
    onSelectCommand(base.length)
  }
  const selectCommand = (i: number) => {
    if (implicit) patch({ commands: declared() })
    onSelectCommand(i)
  }
  const reorder = (from: number, to: number) => {
    if (from === to) return
    const next = declared()
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    // The menu follows the list order; the ★ stays on its button.
    const tb = le.toolbar ? { ...le.toolbar, menu: undefined } : undefined
    patch({ commands: next, toolbar: tb && (tb.primary || tb.button) ? tb : undefined })
    if (selectedCommand === from) onSelectCommand(to)
  }
  const togglePrimary = (name: string) => {
    if (implicit) {
      patch({ commands: declared(), toolbar: { ...(le.toolbar || {}), primary: primary === name ? undefined : name } })
      return
    }
    patch({ toolbar: { ...(le.toolbar || {}), primary: primary === name ? undefined : name } })
  }

  const rows = implicit ? (normalized?.commands || []).filter(c => !c.synthetic) : commands
  const ohc = le.on_header_change
  const ohcDo = ohc ? (ohc.do || 'offer') : 'none'
  const qa = le.quick_add
  const describe = (name: string) => {
    const c = normalized?.commands.find(x => x.name === name)
    return c ? describeCommand(c, labelOf) : ''
  }

  return (
    <div style={{ height: '100%', overflow: 'auto', background: 'var(--color-bg-secondary)' }}>
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 820 }}>
        <h1 style={{ margin: 0, fontSize: 21, fontWeight: 600 }}>
          How lines are filled <span style={{ color: C.muted, fontWeight: 400 }}>— {sm.form_title || `Form ${sm.form_no}`}</span>
        </h1>

        <EveryWay model={model} formIndex={formIndex} rows={rows.map(c => c.label || c.name)}
          headerChange={ohc ? { column: ohc.column, run: ohcDo === 'run', commands: ohc.commands || [] } : null}
          quickAdd={qa ? `${(sources.find(x => x.name === qa.source)?.label) || qa.source}${(qa.match || []).length ? ` · by ${(qa.match || []).join(', ')}` : ''}` : null}
          onSelectNode={onSelectNode} />

        <div style={card}>
          <p style={sec}>Entry view</p>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <label style={opt}>
              <input type="radio" name="le-view" checked={le.view === 'grid'} onChange={() => patch({ view: 'grid' })} />
              <span>Grid<small style={small}>Spreadsheet: paste, fill down, set for many lines</small></span>
            </label>
            <label style={opt}>
              <input type="radio" name="le-view" checked={le.view !== 'grid'} onChange={() => patch({ view: undefined })} />
              <span>One line at a time<small style={small}>Each line in its own form</small></span>
            </label>
          </div>
        </div>

        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <p style={sec}>Sources — where lines come from</p>
            <span style={{ flex: 1 }} />
            <select value="" aria-label="Add source" onChange={e => { if (e.target.value) addSource(e.target.value as LineSourceKind) }}
              style={{ ...btn, paddingRight: 8 }}>
              <option value="">+ Add source</option>
              <option value="transaction">From another transaction</option>
              <option value="master">From a master table</option>
              <option value="business_logic">From business logic</option>
              <option value="process">From a General Process</option>
            </select>
          </div>
          {sources.length === 0 && (
            <span style={{ fontSize: 13, color: C.muted }}>
              No sources yet. A column's "Offer Add … on the lines toolbar" option adds a master source by itself.
            </span>
          )}
          {sources.map((s, i) => (
            <div key={s.name} style={li}>
              <span />
              <span style={{ fontFamily: C.mono, fontSize: 13, fontWeight: 600, color: C.accent,
                border: `1px solid ${C.border}`, borderRadius: 4, textAlign: 'center', padding: '1px 0' }}>
                {KIND_BADGE[s.from.kind]?.tag || '?'}
              </span>
              <span style={{ minWidth: 0 }}>
                <b>{s.label || s.name}</b>{' '}
                <span style={{ color: C.muted }}>· {sourceSummary(s)}</span>
              </span>
              <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
                <button type="button" style={linkBtn} onClick={() => setEditing({ index: i, source: s })}>Edit</button>
                <button type="button" aria-label={`Remove ${s.label || s.name}`} title="Remove source (and its buttons)"
                  onClick={() => removeSource(i)} style={{ ...linkBtn, color: '#c62828', display: 'inline-flex' }}>
                  <Trash2 size={13} />
                </button>
              </span>
            </div>
          ))}
        </div>

        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <p style={sec}>Buttons the user sees</p>
            <span style={{ flex: 1 }} />
            <button type="button" style={btn} onClick={addCommand} disabled={!sources.length}>
              <Plus size={12} /> Add button
            </button>
          </div>
          {implicit && rows.length > 0 && (
            <span style={{ fontSize: 13, color: C.muted }}>
              Each source offers one button of its own. Add buttons to offer a source several ways — fill all,
              select lines, count, from another document.
            </span>
          )}
          {rows.map((c, i) => {
            const isSel = !implicit && selectedCommand === i
            return (
              <div
                key={c.name}
                draggable
                onDragStart={() => setDragFrom(i)}
                onDragOver={e => e.preventDefault()}
                onDrop={() => { if (dragFrom != null) reorder(dragFrom, i); setDragFrom(null) }}
                onClick={() => selectCommand(i)}
                role="button" tabIndex={0}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectCommand(i) } }}
                aria-pressed={isSel}
                style={{ ...li, cursor: 'pointer', borderColor: isSel ? C.accent : C.border,
                  boxShadow: isSel ? '0 0 0 2px rgba(0,97,208,.15)' : undefined }}
              >
                <span aria-hidden="true" style={{ color: C.muted, cursor: 'grab', letterSpacing: -2 }}>⋮⋮</span>
                <button type="button" title={primary === c.name ? 'Shown on the button face' : 'Show on the button face'}
                  aria-label={`${c.label || c.name} on the button face`}
                  onClick={e => { e.stopPropagation(); togglePrimary(c.name) }}
                  style={{ ...linkBtn, color: primary === c.name ? '#f59e0b' : C.muted, display: 'inline-flex', justifyContent: 'center' }}>
                  <Star size={14} fill={primary === c.name ? '#f59e0b' : 'none'} />
                </button>
                <span style={{ minWidth: 0 }}>
                  <b>{c.label || c.name}</b>{' '}
                  <span style={{ color: C.muted }}>· {(sources.find(s => s.name === c.source)?.label) || c.source} · {describe(c.name)}</span>
                </span>
                <span style={{ fontFamily: C.mono, fontSize: 13, color: C.muted }}>{c.shortcut || ''}</span>
              </div>
            )
          })}
          {layout && (layout.primary || layout.menu.length > 0) && (
            <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap', marginTop: 4,
              paddingTop: 12, borderTop: `1px dashed ${C.border}` }}>
              <span style={{ fontSize: 13, color: C.muted, width: 60 }}>Preview</span>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <LineEntryMenu layout={layout} busy={null} onRun={() => {}} describe={describe} previewOpen />
              </div>
              <span style={{ fontSize: 13, color: C.muted, flex: 1, minWidth: 180 }}>
                Shown the same way on the spreadsheet grid, the table view's lines tab and the form view's lines section.
              </span>
            </div>
          )}
          {rows.length > 1 && (
            <label style={fld}>Label on the button face when no button is starred
              <input style={inp} value={le.toolbar?.button || ''} placeholder={layout?.buttonLabel || ''}
                onChange={e => patch({ toolbar: { ...(le.toolbar || {}), button: e.target.value || undefined } })} />
            </label>
          )}
        </div>

        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <p style={sec}>When</p>
            <select aria-label="Header column" value={ohc?.column || ''}
              onChange={e => patch({ on_header_change: e.target.value
                ? { column: e.target.value, do: ohc?.do || 'offer', commands: ohc?.commands || [] } : undefined })}
              style={{ ...inp, width: 'auto', height: 26, fontSize: 13 }}>
              <option value="">a header column…</option>
              {parentNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <p style={sec}>on the header changes</p>
          </div>
          {ohc && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <label style={opt}>
                <input type="radio" name="le-hc" checked={ohcDo === 'run'} onChange={() => patch({ on_header_change: { ...ohc, do: 'run' } })} />
                <span>Run a button automatically
                  {ohcDo === 'run' && (
                    <select aria-label="Button to run" value={ohc.commands?.[0] || ''}
                      onChange={e => patch({ on_header_change: { ...ohc, commands: e.target.value ? [e.target.value] : [] } })}
                      style={{ ...inp, width: 'auto', height: 26, fontSize: 13, marginLeft: 8 }}>
                      <option value="">— choose —</option>
                      {commandNames.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  )}
                </span>
              </label>
              <label style={opt}>
                <input type="radio" name="le-hc" checked={ohcDo === 'offer'} onChange={() => patch({ on_header_change: { ...ohc, do: 'offer' } })} />
                <span>Offer buttons in a banner above the lines
                  {(ohc.commands || []).length > 0 && <>: {(ohc.commands || []).map((n, i) => <b key={n}>{i ? ', ' : ''}{n}</b>)}</>}
                  <small style={small}>The user decides each time</small>
                  {ohcDo === 'offer' && (
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6 }}>
                      {commandNames.map(n => (
                        <label key={n} style={{ fontSize: 13, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                          <input type="checkbox" checked={(ohc.commands || []).includes(n)}
                            onChange={e => patch({ on_header_change: { ...ohc, commands: e.target.checked
                              ? [...(ohc.commands || []), n] : (ohc.commands || []).filter(x => x !== n) } })} /> {n}
                        </label>
                      ))}
                    </span>
                  )}
                </span>
              </label>
              <label style={opt}>
                <input type="radio" name="le-hc" checked={ohcDo === 'none'} onChange={() => patch({ on_header_change: { ...ohc, do: 'none' } })} />
                <span>Do nothing</span>
              </label>
            </div>
          )}
        </div>

        <div style={card}>
          <p style={sec}>Quick add — scan or type a code</p>
          <label style={opt}>
            <input type="checkbox" checked={!!qa} disabled={!sources.length}
              onChange={e => patch({ quick_add: e.target.checked
                ? { source: (sources.find(s => s.from?.kind === 'master') || sources[0]).name, match: [], existing: 'increment' }
                : undefined })} />
            <span>Show a scan bar above the lines<small style={small}>12*ITM-014 adds 12; a code already on a line adds to it</small></span>
          </label>
          {qa && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
              <label style={fld}>Source
                <select style={inp} value={qa.source} onChange={e => patch({ quick_add: { ...qa, source: e.target.value } })}>
                  {sources.map(s => <option key={s.name} value={s.name}>{s.label || s.name}</option>)}
                </select>
              </label>
              <label style={fld}>Match the code on (source columns)
                <input style={inp} value={(qa.match || []).join(', ')} placeholder="ITEM_CODE, BARCODE"
                  onChange={e => patch({ quick_add: { ...qa, match: e.target.value.split(',').map(x => x.trim()).filter(Boolean) } })} />
              </label>
              <label style={fld}>Code already on a line
                <select style={inp} value={qa.existing || 'increment'}
                  onChange={e => patch({ quick_add: { ...qa, existing: e.target.value as 'increment' | 'new_line' } })}>
                  <option value="increment">Add to its quantity</option>
                  <option value="new_line">Add a new line</option>
                </select>
              </label>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <LineSourceEditor
          open
          source={editing.source}
          takenNames={sources.filter((_, j) => j !== editing.index).map(s => s.name)}
          lineSm={sm}
          parentSm={parentSm}
          model={model}
          project={project}
          formNo={String(sm.form_no || formIndex + 1)}
          commandNames={(normalized?.commands || []).filter(c => c.source === editing.source.name).map(c => c.name)}
          onClose={() => setEditing(null)}
          onSave={saveSource}
        />
      )}
    </div>
  )
}

// ── right: the selected button's properties ──────────────────────────────

interface CommandProps {
  model: TransactionModel
  formIndex: number
  parentSm: SqlModel | null
  project: string
  commandIndex: number | undefined
  onModelChange: (m: TransactionModel) => void
  onSelectCommand: (index: number | undefined) => void
}

export function LineCommandPanel({
  model, formIndex, parentSm, project, commandIndex, onModelChange, onSelectCommand,
}: CommandProps) {
  const sm = model.sql_models[formIndex]?.sql_model
  const le: LineEntry = sm?.line_entry || {}
  const commands = le.commands || []
  const sources = le.sources || []
  const cmd = commandIndex != null ? commands[commandIndex] : undefined
  const [testing, setTesting] = useState<SourceTab | null>(null)
  const normalized = useMemo(() => (sm ? lineEntryOf(sm) : null), [sm])

  if (!sm || !cmd || commandIndex == null) {
    return (
      <div style={{ padding: 16, fontSize: 14, color: C.muted, lineHeight: 1.5 }}>
        <b style={{ color: 'var(--color-text)', display: 'block', marginBottom: 6 }}>Line Entry</b>
        Select a button in the centre to edit it here, or a source's <b>Edit</b> to change where lines come from.
      </div>
    )
  }
  const src = sources.find(s => s.name === cmd.source)
  const norm = normalized?.commands.find(c => c.name === cmd.name)
  const labelOf = labelsOf(sm)
  const docCol = src?.documents?.header_column
  const canChoose = !!src?.documents?.choose?.lookup
  const primary = le.toolbar?.primary === cmd.name

  const write = (p: Partial<LineEntry>) => writeLineEntry(model, formIndex, { ...le, ...p }, onModelChange)
  const edit = (p: Partial<LineCommand>) => {
    const next = commands.map((c, j) => (j === commandIndex ? { ...c, ...p } : c))
    if (p.name && p.name !== cmd.name) {
      const old = cmd.name
      write({
        commands: next,
        toolbar: le.toolbar ? { ...le.toolbar, primary: le.toolbar.primary === old ? p.name : le.toolbar.primary,
          menu: le.toolbar.menu?.map(n => (n === old ? p.name! : n)) } : undefined,
        on_header_change: le.on_header_change ? { ...le.on_header_change,
          commands: (le.on_header_change.commands || []).map(n => (n === old ? p.name! : n)) } : undefined,
      })
      return
    }
    write({ commands: next })
  }
  const editSource = (p: Partial<LineSource>) => write({
    sources: sources.map(s => (s.name === cmd.source ? { ...s, ...p } : s)),
  })
  const remove = () => {
    write({
      commands: commands.filter((_, j) => j !== commandIndex),
      toolbar: primary && le.toolbar ? { ...le.toolbar, primary: undefined } : le.toolbar,
    })
    onSelectCommand(undefined)
  }

  // Columns a button may pre-fill: those the source limits or fills, and any
  // this button already fills.
  const fillCols = Array.from(new Set([
    ...Object.keys(src?.limits || {}), ...Object.keys(src?.fill || {}), ...Object.keys(cmd.fill || {}),
  ]))
  const fillChoice = (col: string): string => {
    const v = (cmd.fill || {})[col]
    if (v == null) return ''
    const t = String(v).trim().toLowerCase()
    return t === 'max' || t === 'min' || t === '0' ? t : 'custom'
  }
  const setFill = (col: string, v: string | null) => {
    const next = { ...(cmd.fill || {}) }
    if (v == null) delete next[col]
    else next[col] = v
    edit({ fill: next })
  }
  const lineNames = (sm.columns || []).map(w => w?.column?.name).filter(Boolean) as string[]
  const docNoun = docCol ? labelOf(docCol) : 'document'
  const radios = (name: string, value: string, items: Array<[string, string]>, onPick: (v: string) => void) => (
    items.map(([v, label]) => (
      <label key={v} style={opt}>
        <input type="radio" name={name} checked={value === v} onChange={() => onPick(v)} /><span>{label}</span>
      </label>
    ))
  )

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14, overflow: 'auto', height: '100%', boxSizing: 'border-box' }}>
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Button: {cmd.label || cmd.name}</h2>
      <label style={fld}>Label<input style={inp} value={cmd.label || ''} onChange={e => edit({ label: e.target.value })} /></label>
      <label style={fld}>Name (used by shortcuts and the header banner)
        <input style={{ ...inp, fontFamily: C.mono, borderColor: commands.some((c, j) => j !== commandIndex && c.name === cmd.name) ? '#c62828' : undefined }}
          value={cmd.name} onChange={e => edit({ name: e.target.value.replace(/\s+/g, '_') })} />
      </label>
      <label style={fld}>Source
        <select style={inp} value={cmd.source} onChange={e => edit({ source: e.target.value })}>
          {sources.map(s => <option key={s.name} value={s.name}>{s.label || s.name}</option>)}
        </select>
      </label>

      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <legend style={{ ...fld, marginBottom: 6 }}>Which documents</legend>
        {radios('le-doc', norm?.documents || 'none', [
          ...(docCol ? [['header', `This record's ${docNoun} (${docCol})`] as [string, string]] : []),
          ...(canChoose ? [['choose', 'Let the user choose'] as [string, string]] : []),
          ...(docCol && canChoose ? [['header_or_choose', `This record's ${docNoun}, or choose another`] as [string, string]] : []),
          ...(!docCol && !canChoose ? [['none', 'None — the source needs no document'] as [string, string]] : []),
        ], v => edit({ documents: v as LineCommand['documents'] }))}
        {!canChoose && (
          <small style={small}>To let the user choose, give the source a document chooser (Edit the source → Documents).</small>
        )}
      </fieldset>
      {canChoose && (norm?.documents === 'choose' || norm?.documents === 'header_or_choose') && (
        <>
          <div style={fld}>Documents the user can choose from
            <div style={{ ...inp, height: 'auto', minHeight: 32, padding: '6px 10px', fontFamily: C.mono, fontSize: 13, background: 'var(--color-bg-secondary)' }}>
              {src?.documents?.choose?.lookup}{src?.documents?.choose?.filter ? <><br />{src.documents.choose.filter}</> : null}
            </div>
          </div>
          <label style={opt}>
            <input type="checkbox" checked={src?.documents?.choose?.multi !== false}
              onChange={e => editSource({ documents: { ...(src?.documents || {}), choose: { ...(src!.documents!.choose!), multi: e.target.checked } } })} />
            <span>Allow several at once</span>
          </label>
        </>
      )}

      <fieldset style={{ border: 0, padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <legend style={{ ...fld, marginBottom: 6 }}>How the user picks lines</legend>
        {radios('le-pk', norm?.picker || 'review', [
          ['none', 'Add every line, no dialog'],
          ['review', 'Tick lines and change what differs'],
          ['multi', 'Multi-select (one line each)'],
        ], v => edit({ picker: v as LineCommand['picker'] }))}
      </fieldset>

      {fillCols.map(col => {
        const ch = fillChoice(col)
        return (
          <label key={col} style={fld}>Pre-fill {labelOf(col)}
            <select style={inp} value={ch} onChange={e => {
              const v = e.target.value
              setFill(col, v === '' ? null : v === 'custom' ? (cmd.fill?.[col] && fillChoice(col) === 'custom' ? cmd.fill[col] : `{src.${col}}`) : v)
            }}>
              <option value="">As the source says</option>
              <option value="max">Its max{src?.limits?.[col]?.max ? ` (${src.limits[col].max})` : ''}</option>
              <option value="min">Its min{src?.limits?.[col]?.min ? ` (${src.limits[col].min})` : ''}</option>
              <option value="0">Blank (0) — the user counts</option>
              <option value="custom">An expression…</option>
            </select>
            {ch === 'custom' && (
              <input style={{ ...inp, fontFamily: C.mono }} value={cmd.fill?.[col] || ''} onChange={e => setFill(col, e.target.value)} />
            )}
          </label>
        )
      })}
      <select aria-label="Pre-fill another column" value="" style={{ ...inp, width: 'auto', color: C.muted }}
        onChange={e => { if (e.target.value) setFill(e.target.value, 'max') }}>
        <option value="">+ pre-fill another column…</option>
        {lineNames.filter(n => !fillCols.includes(n)).map(n => <option key={n} value={n}>{n}</option>)}
      </select>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label style={fld}>Show on
          <select style={inp} value={primary ? 'face' : 'menu'}
            onChange={e => write({ toolbar: { ...(le.toolbar || {}), primary: e.target.value === 'face' ? cmd.name : undefined } })}>
            <option value="face">Button face</option>
            <option value="menu">Menu</option>
          </select>
        </label>
        <label style={fld}>Shortcut
          <input style={inp} value={cmd.shortcut || ''} placeholder="e.g. Alt O" onChange={e => edit({ shortcut: e.target.value || undefined })} />
        </label>
      </div>
      <label style={fld}>Line under the label in the menu
        <input style={inp} value={cmd.description || ''} placeholder={norm ? describeCommand({ ...norm, description: undefined }, labelOf) : ''}
          onChange={e => edit({ description: e.target.value || undefined })} />
      </label>
      <label style={fld}>Check before adding
        <select style={inp} value={cmd.validate || ''} onChange={e => edit({ validate: (e.target.value || undefined) as LineCommand['validate'] })}>
          <option value="">As the source says</option>
          <option value="limits">Only the limits</option>
          <option value="object">This form's validations, per line</option>
        </select>
      </label>
      <div style={fld}>Who can use it
        <span style={{ ...inp, display: 'flex', alignItems: 'center', background: 'var(--color-bg-secondary)' }}>
          Anyone who can add lines (and read the source)
        </span>
      </div>
      <label style={fld}>Show only when
        <input style={{ ...inp, fontFamily: C.mono }} value={cmd.visible_when || ''} placeholder="{form.GRN_STATUS} == 'OPEN'"
          onChange={e => edit({ visible_when: e.target.value || undefined })} />
      </label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', paddingTop: 6, borderTop: `1px solid ${C.border}` }}>
        <button type="button" style={btn} disabled={!src} onClick={() => setTesting('test')}>Test with a sample record</button>
        <span style={{ flex: 1 }} />
        <button type="button" style={{ ...btn, color: '#c62828' }} onClick={remove}>Delete</button>
      </div>

      {testing && src && (
        <LineSourceEditor
          open
          source={src}
          takenNames={sources.filter(s => s.name !== src.name).map(s => s.name)}
          lineSm={sm}
          parentSm={parentSm}
          model={model}
          project={project}
          formNo={String(sm.form_no || formIndex + 1)}
          commandNames={(normalized?.commands || []).filter(c => c.source === src.name).map(c => c.name)}
          initialTab={testing}
          initialCommand={cmd.name}
          onClose={() => setTesting(null)}
          onSave={(s) => {
            // A renamed source keeps its buttons.
            write({
              sources: sources.map(x => (x.name === src.name ? s : x)),
              commands: s.name !== src.name
                ? commands.map(c => (c.source === src.name ? { ...c, source: s.name } : c)) : commands,
            })
            setTesting(null)
          }}
        />
      )}
    </div>
  )
}


// ── every way a row reaches this form ─────────────────────────────────────
//
// One list of every route a row takes onto a line form, wherever each one is
// defined: this panel's own line buttons, the header-change trigger and quick
// add, a Pick-rows (select_grid) button on any form, a column's "Add …" lookup
// on the lines toolbar, and typing a row by hand (the form's List settings).
function EveryWay({ model, formIndex, rows, headerChange, quickAdd, onSelectNode }: {
  model: TransactionModel
  formIndex: number
  rows: string[]
  headerChange: { column: string; run: boolean; commands: string[] } | null
  quickAdd: string | null
  onSelectNode?: (node: TreeNode) => void
}) {
  const entries = allSqlModels(model)
  const sm = entries[formIndex]?.sm
  if (!sm) return null
  const formNo = entries[formIndex].formNo
  const firstChild = childFormsOf(model, headerFormNo(model))[0]?.formNo
  type Way = { title: string; detail: string; where: string; node?: TreeNode }
  const ways: Way[] = []
  if (rows.length) ways.push({ title: 'Line buttons', detail: rows.join(' · '), where: 'Line entry, below' })
  if (headerChange?.column) {
    ways.push({
      title: `When the header's ${headerChange.column} changes`,
      detail: headerChange.run ? `Runs ${headerChange.commands[0] || '(no button chosen)'}` : headerChange.commands.length ? `Offers ${headerChange.commands.join(', ')}` : 'Does nothing',
      where: 'Line entry, below',
    })
  }
  if (quickAdd) ways.push({ title: 'Quick add — scan or type a code', detail: `From ${quickAdd}`, where: 'Line entry, below' })
  const pickRows = (list: unknown[] | undefined, node: (i: number) => TreeNode, scope: string) => {
    ;(list || []).forEach((a, i) => {
      const d = legacyActionToDef(a as Parameters<typeof legacyActionToDef>[0])
      if (d.handler !== 'select_grid') return
      const target = String(d.target_form_no || firstChild || '')
      if (target !== formNo) return
      ways.push({ title: `${d.name || 'Pick rows'} (button)`, detail: `Select rows from a grid → ${sm.form_title || formNo}`, where: `${scope} › Buttons`, node: node(i) })
    })
  }
  pickRows(model.actions, (i) => ({ type: 'object-actions', actionIndex: i, label: 'Record buttons' }), 'Whole record')
  entries.forEach((e, fi) => pickRows(e.sm.actions, (i) => ({ type: 'form-actions', formIndex: fi, actionIndex: i, label: 'Form buttons' }), e.sm.form_title || `Form ${e.formNo}`))
  ;(sm.columns || []).forEach((w, ci) => {
    const c = w?.column
    const v = c?.lookup_add_lines
    if (!c || !(v === true || v === 'true') || !(c.lookup || '').trim()) return
    ways.push({ title: `“Add ${c.heading || c.name}…” on the lines toolbar`, detail: `From the ${c.name} lookup`, where: `Column ${c.name} › Behaviour`,
      node: { type: 'column', formIndex, columnIndex: ci, label: c.name, tab: 'behaviour' } })
  })
  const allowAdd = sm.list_view?.allow_add !== false
  ways.push({ title: 'Typed by hand', detail: allowAdd ? `Allow add is on · ${sm.list_view?.page_size || 50} rows per page` : 'Allow add is off — rows only arrive the ways above',
    where: 'Form › List', node: { type: 'form', formIndex, label: sm.form_title || '', tab: 'list' } })
  return (
    <div style={{ ...card, padding: 0, gap: 0 }}>
      <div style={{ padding: '14px 18px', borderBottom: `1px solid ${C.border}` }}>
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600 }}>Every way a row reaches {sm.form_title || `Form ${formNo}`}</h2>
        <div style={{ fontSize: 14, color: C.muted, marginTop: 2 }}>One list, wherever each one is defined. Edit opens it.</div>
      </div>
      {ways.map((w, i) => (
        <div key={i} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px 14px', padding: '11px 18px', borderBottom: i < ways.length - 1 ? `1px solid ${C.border}` : 'none' }}>
          <span style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--color-text)', color: 'var(--color-bg-secondary)', fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>{i + 1}</span>
          <span style={{ display: 'flex', flexDirection: 'column', flex: '1 1 260px', minWidth: 0 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>{w.title}</span>
            <span style={{ fontSize: 14, color: C.muted }}>{w.detail}</span>
          </span>
          <span style={{ fontSize: 14, background: 'var(--color-surface-2)', borderRadius: 4, padding: '2px 8px', whiteSpace: 'nowrap' }}>{w.where}</span>
          {w.node && onSelectNode
            ? <button type="button" style={linkBtn} onClick={() => onSelectNode(w.node!)}>Edit</button>
            : <span style={{ width: 28 }} />}
        </div>
      ))}
    </div>
  )
}
